import { resolveCdfComponentInspections } from 'features/libra/ticket/cdfComponentInspection'
import type {
  GeckoComponentDataFrame,
  GeckoComponentDataset,
  GeckoEntry,
} from 'types'
import { parseDate } from 'utils/parsingHelpers'

/**
 * Component + inspection selection helpers extracted from the (soon-deleted) lazy heal
 * hook `useEnsureComponentScopes.ts`. Callers (e.g. `TabbedWorkspace` / `UnitComponentTabs`)
 * use these at render time to derive:
 *
 *   - a deterministic tab-ordered component list (`sortComponentsForTabs`)
 *   - the most-recent inspection per component (`pickMostRecentInspection`)
 *   - the combined `componentId → inspectionId` map with inspection-less components
 *     filtered out (`buildInspectionsByComponentId`)
 *
 * Semantics MUST match the old heal exactly, because downstream FFS readers assume the
 * same rules:
 *   - Sort by `data.name ?? id`, then by `id`, using codepoint comparison (NOT
 *     `localeCompare` — locale-dependent order would let two clients disagree).
 *   - Rank inspections by RAW `entry.raw.data.date` (unparsed string). Fall back to
 *     `entry.updated_at` only when the raw date is missing/unparseable. ANY parseable-date
 *     entry beats ANY fallback-only entry, regardless of key strings.
 *   - Components with no inspection get NO entry in the resulting map (downstream code
 *     treats missing `inspectionId` as a hard error; tabs filter them out).
 */

/** Minimal shape of a component entry needed for tab ordering. */
export interface ComponentForTabs {
  id: string
  data: { name?: string }
}

/**
 * Codepoint (not locale) compare. Guarantees the same order for the same data across
 * clients running in different locales.
 */
function codepointCompare(a: string, b: string): number {
  if (a < b) return -1
  if (a > b) return 1
  return 0
}

/**
 * Deterministic tab ordering. Sort key: `data.name` if present, otherwise the component
 * `id` (so unnamed components don't all sort to the front under a shared empty string).
 * Tiebreak by id.
 */
export function sortComponentsForTabs<T extends ComponentForTabs>(
  components: readonly T[]
): T[] {
  return [...components].sort((a, b) => {
    const keyA = a.data.name ?? a.id
    const keyB = b.data.name ?? b.id
    const keyCmp = codepointCompare(keyA, keyB)
    return keyCmp !== 0 ? keyCmp : codepointCompare(a.id, b.id)
  })
}

interface RankKey {
  hasValidDate: boolean
  value: string
}

function rankKey(e: GeckoEntry): RankKey {
  // Use `entry.raw.data.date` (unparsed JSON string). The parsed `GeckoEntry`
  // may have already coerced `data.date` to a `Date` (or `new Date()` when missing),
  // so calling parseDate on the parsed value would silently degrade to the
  // `updated_at` fallback for every entry.
  const rawDate = (e.raw.data as { date?: unknown })?.date
  const parsedDate = parseDate(rawDate)
  if (parsedDate) return { hasValidDate: true, value: parsedDate.toISOString() }
  return { hasValidDate: false, value: e.updated_at }
}

function compareRank(a: RankKey, b: RankKey): number {
  if (a.hasValidDate !== b.hasValidDate) return a.hasValidDate ? 1 : -1
  if (a.value > b.value) return 1
  if (a.value < b.value) return -1
  return 0
}

/**
 * Pick the most-recent inspection id for a component from a pool of raw inspection
 * entries. Returns `undefined` when the component has no inspection — callers filter
 * these out of the tab list (see `buildInspectionsByComponentId`).
 *
 * @param componentId The component to filter by (via `entry.tags.component`).
 * @param inspectionEntries Raw Rolodex inspection entries (from
 *   `useLibraAssetModelDataForUnit(unitId).inspectionEntries`).
 */
export function pickMostRecentInspection(
  componentId: string,
  inspectionEntries: readonly GeckoEntry[]
): string | undefined {
  const componentInspections = inspectionEntries.filter(
    (e) => e.tags.component === componentId
  )
  const best = componentInspections.reduce<GeckoEntry | undefined>(
    (acc, current) => {
      if (!acc) return current
      const cmp = compareRank(rankKey(current), rankKey(acc))
      if (cmp > 0) return current
      if (cmp < 0) return acc
      // Tiebreaker on equal primary anchor: lexicographically smaller id.
      return current.id < acc.id ? current : acc
    },
    undefined
  )
  return best?.id
}

/**
 * Reduce a bucket of inspection entries down to the single most-recent one
 * (or `undefined` for an empty bucket) using the same rank as
 * `pickMostRecentInspection`.
 */
function reduceToMostRecent(
  entries: readonly GeckoEntry[]
): GeckoEntry | undefined {
  return entries.reduce<GeckoEntry | undefined>((acc, current) => {
    if (!acc) return current
    const cmp = compareRank(rankKey(current), rankKey(acc))
    if (cmp > 0) return current
    if (cmp < 0) return acc
    return current.id < acc.id ? current : acc
  }, undefined)
}

/**
 * Build a `componentId → Set<inspectionId>` index of the inspections that
 * actually carry component-data-frame (CDF) data, in one pass over the CDF and
 * CDS pools. Uses the shared `resolveCdfComponentInspections` resolver so the
 * inspection the tab picks agrees with the CDFs `useFfsComponentCdfs` will find.
 */
export function buildInspectionsWithCdfData(
  componentDataFrames: ReadonlyArray<GeckoComponentDataFrame>,
  componentDatasets: ReadonlyArray<GeckoComponentDataset>
): Map<string, Set<string>> {
  const datasetById = new Map<string, GeckoComponentDataset>(
    componentDatasets.map((cds) => [cds.id, cds])
  )
  const index = new Map<string, Set<string>>()
  for (const cdf of componentDataFrames) {
    for (const { component, inspection } of resolveCdfComponentInspections(
      cdf,
      datasetById
    )) {
      const set = index.get(component)
      if (set) {
        set.add(inspection)
      } else {
        index.set(component, new Set([inspection]))
      }
    }
  }
  return index
}

/**
 * Build a `componentId → inspectionId` record for use inside `ComponentScopeContext`.
 * Components with no inspection are omitted entirely — callers should
 * likewise filter them out of the tab list. Returns a plain Record because
 * the sole consumer chain (`TabbedWorkspace` → `UnitComponentTabs` →
 * `ComponentScopeProvider`) passes it through props.
 *
 * When the optional CDF/CDS pools are supplied, each component prefers the
 * most-recent inspection that actually has CDF data (see
 * `buildInspectionsWithCdfData`), falling back to the most-recent inspection by
 * date when none of its inspections carry data. This matters because a
 * component can have a newer, freshly-created inspection with no ingested data
 * while its real scans live under an older inspection; ranking purely by date
 * would select the empty one and every downstream FFS step (viz, binning,
 * triage, min-thickness) would read an empty inspection. Omitting the pools
 * preserves the original date-only behavior.
 *
 * Single-pass group + reduce (O(components + inspections + cdfs + cdss)) rather
 * than the naive O(components x inspections) — matters when a unit carries many
 * inspections.
 */
export function buildInspectionsByComponentId<T extends ComponentForTabs>(
  components: readonly T[],
  inspectionEntries: readonly GeckoEntry[],
  componentDataFrames: ReadonlyArray<GeckoComponentDataFrame> = [],
  componentDatasets: ReadonlyArray<GeckoComponentDataset> = []
): Record<string, string> {
  // Group inspections by their `tags.component` in one pass. Entries with no
  // component tag are dropped.
  const byComponent = new Map<string, GeckoEntry[]>()
  for (const entry of inspectionEntries) {
    const componentId = entry.tags.component
    if (!componentId) continue
    const bucket = byComponent.get(componentId)
    if (bucket) {
      bucket.push(entry)
    } else {
      byComponent.set(componentId, [entry])
    }
  }
  // Build the "inspections that have CDF data" index once (empty when the pools
  // are omitted, which collapses to the original date-only ranking below).
  const inspectionsWithData = buildInspectionsWithCdfData(
    componentDataFrames,
    componentDatasets
  )
  // Rank each bucket. Iterate `components` (not the grouped map) so the
  // caller's ordering is preserved and unknown-component inspections don't
  // leak into the output.
  const out: Record<string, string> = {}
  for (const component of components) {
    const bucket = byComponent.get(component.id)
    if (!bucket) continue
    // Prefer inspections that carry CDF data; only fall back to the full bucket
    // (date-only ranking) when none of this component's inspections have data.
    const dataInspectionIds = inspectionsWithData.get(component.id)
    const withData = dataInspectionIds
      ? bucket.filter((e) => dataInspectionIds.has(e.id))
      : []
    const winner = reduceToMostRecent(withData.length > 0 ? withData : bucket)
    if (winner) out[component.id] = winner.id
  }
  return out
}
